import React, { useState, useEffect } from 'react';
import { FontSizeOutlined } from '@ant-design/icons';
import { Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import './style.scss';

/**
 * A component that allows the user to change the font size of the app.
 *
 * @component
 * @example
 * return (
 *   <FontSizeChanger />
 * )
 */
function FontSizeChanger() {
	const [fontSize, setFontSize] = useState(16);

	/**
	 * A function that translates a given string to the selected language.
	 *
	 * @function
	 * @name t
	 * @memberof module:Translation
	 * @returns {string} The translated string.
	 *
	 * @example
	 * const { t } = useTranslation();
	 * const translatedString = t('hello');
	 */
	const { t } = useTranslation();

	/**
	 * Updates the font size state variable by incrementing it by 1.
	 *
	 * @function
	 * @memberof FontSizeChanger
	 * @inner
	 */
	const handleIncreaseFontSize = () => {
		setFontSize((prevFontSize) => prevFontSize + 1);
	};

	/**
	 * Updates the font size state variable by decrementing it by 1.
	 *
	 * @function
	 * @memberof FontSizeChanger
	 * @inner
	 */
	const handleDecreaseFontSize = () => {
		setFontSize((prevFontSize) => prevFontSize - 1);
	};

	useEffect(() => {
		document.documentElement.style.fontSize = `${fontSize}px`;
	}, [fontSize]);

	return (
		<>
			<Tooltip title={t('increaseFont')}>
				<button
					id='increase-font-ac'
					className='accessibility-btn'
					aria-label={t('increaseFont')}
					tabIndex={0}
					onClick={handleIncreaseFontSize}
				>
					<div className='accessibility-btn__style'>
						<FontSizeOutlined />
						<span className='accessibility-size'>+</span>
					</div>
				</button>
			</Tooltip>
			<Tooltip title={t('decreaseFont')}>
				<button
					id='decrease-font-ac'
					className='accessibility-btn'
					aria-label={t('decreaseFont')}
					tabIndex={0}
					onClick={handleDecreaseFontSize}
				>
					<div className='accessibility-btn__style'>
						<FontSizeOutlined />
						<span className='accessibility-size'>-</span>
					</div>
				</button>
			</Tooltip>
		</>
	);
}

export default FontSizeChanger;
